import { useEffect, useState } from "react";
import { Cookie, ShieldCheck } from "lucide-react";

const STORAGE_KEY = "mk_cookie_consent";
const GA4_ID = process.env.REACT_APP_GA4_ID || "";

const loadGA = () => {
  if (!GA4_ID.startsWith("G-") || window.__ga4_loaded) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);
  window.gtag("config", GA4_ID, { anonymize_ip: true });
  window.__ga4_loaded = true;
};

export const applyConsent = (c) => {
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: c.analytics ? "granted" : "denied",
      ad_storage: c.marketing ? "granted" : "denied",
      ad_user_data: c.marketing ? "granted" : "denied",
      ad_personalization: c.marketing ? "granted" : "denied",
    });
  }
  if (c.analytics) loadGA();
};

const hasStoredConsent = () => {
  try { return Boolean(localStorage.getItem(STORAGE_KEY)); } catch { return false; }
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(() => !hasStoredConsent());
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    document.documentElement.removeAttribute("data-consent");
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        applyConsent(JSON.parse(stored));
      } else {
        setVisible(true);
      }
    } catch {
      setVisible(true);
    }
    const open = () => {
      setShowPrefs(true);
      setVisible(true);
    };
    window.addEventListener("open-cookie-preferences", open);
    return () => window.removeEventListener("open-cookie-preferences", open);
  }, []);

  const save = (c) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...c, ts: Date.now() }));
    } catch {}
    applyConsent(c);
    setVisible(false);
    setShowPrefs(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      // Op mobiel positioneren we de banner boven de sticky CTA (76px hoog + veilige marge).
      // Op desktop blijft hij gewoon onderaan.
      className="fixed bottom-[calc(76px+env(safe-area-inset-bottom,0px))] md:bottom-0 inset-x-0 z-[90] px-2 sm:p-4"
      role="dialog"
      aria-label="Cookievoorkeuren"
    >
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-[0_16px_50px_-12px_rgba(15,23,42,0.35)] ring-1 ring-black/10 p-3 sm:p-5">
        <div className="flex items-start gap-3">
          <span className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-lg bg-maas-soft text-maas shrink-0">
            <Cookie className="h-4.5 w-4.5" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] sm:text-sm font-semibold text-slate-900">Cookies op maaskozijn.nl</p>
            <p className="mt-1 text-[11.5px] sm:text-xs text-slate-600 leading-snug sm:leading-relaxed line-clamp-2 sm:line-clamp-none">
              Wij gebruiken noodzakelijke cookies. Met uw toestemming ook analytische cookies om de website te verbeteren.{" "}
              <a href="/privacy" className="text-maas underline">Privacybeleid</a>.
            </p>

            {showPrefs && (
              <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                <label className="flex items-center justify-between gap-3 text-xs text-slate-700">
                  <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-maas" /> Noodzakelijk (altijd actief)</span>
                  <input type="checkbox" checked disabled className="accent-maas" />
                </label>
                <label className="flex items-center justify-between gap-3 text-xs text-slate-700">
                  <span>Analytisch (Google Analytics, anoniem)</span>
                  <input
                    data-testid="cookie-toggle-analytics"
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="accent-maas h-4 w-4"
                  />
                </label>
                <label className="flex items-center justify-between gap-3 text-xs text-slate-700">
                  <span>Marketing (advertentiemeting)</span>
                  <input
                    data-testid="cookie-toggle-marketing"
                    type="checkbox"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    className="accent-maas h-4 w-4"
                  />
                </label>
              </div>
            )}

            <div className="mt-2.5 sm:mt-3 flex flex-wrap items-center gap-1.5 sm:gap-2">
              <button
                data-testid="cookie-accept-all"
                onClick={() => save({ analytics: true, marketing: true })}
                className="px-3 sm:px-4 py-2 rounded-lg bg-gold hover:bg-gold-hover text-white text-[11.5px] sm:text-xs font-semibold transition-colors"
              >
                Alles accepteren
              </button>
              <button
                data-testid="cookie-necessary-only"
                onClick={() => save({ analytics: false, marketing: false })}
                className="px-3 sm:px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-[11.5px] sm:text-xs font-semibold transition-colors"
              >
                Alleen noodzakelijk
              </button>
              {showPrefs ? (
                <button
                  data-testid="cookie-save-prefs"
                  onClick={() => save({ analytics, marketing })}
                  className="px-3 sm:px-4 py-2 rounded-lg bg-maas hover:bg-maas-deep text-white text-[11.5px] sm:text-xs font-semibold transition-colors"
                >
                  Voorkeuren opslaan
                </button>
              ) : (
                <button
                  data-testid="cookie-open-prefs"
                  onClick={() => setShowPrefs(true)}
                  className="px-2 py-2 text-[11.5px] sm:text-xs text-slate-500 hover:text-maas underline"
                >
                  Voorkeuren
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
